import { useEffect } from 'react';
import { PUSHER_STATES, PUSHER_STATE_MESSAGES } from '@/context/PusherContext/PusherContextConstants';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';

/**
 * Wire shape of HIGHLIGHT_REEL_GENERATION_STATUS. The BE publishes the project as event_id and
 * the content as broadcast_id on every state; DONE additionally carries the new reel's clip_id
 * (the Asset id, not the underlying video asset) and title.
 */
export interface HighlightReelGenerationStatusData {
  state: string;
  event_id?: string;
  broadcast_id?: string;
  clip_id?: string;
  title?: string;
}

/**
 * Reacts to HIGHLIGHT_REEL_GENERATION_STATUS terminal events for the broadcast that is currently
 * open. PROCESSING never reaches here — PusherContext only dispatches DONE and FAILED.
 *
 * On DONE it asks for a reels refetch the same way ReloadAllClips does for clips: dispatch an
 * event, and let whoever owns the query call its own refetch. That keeps the query key out of
 * this hook and works regardless of which QueryClient the tree was built with.
 */
export function useHighlightReelGenerationStatus(broadcastId?: string) {
  useEffect(() => {
    if (!broadcastId) {
      return;
    }

    const handler = (data: HighlightReelGenerationStatusData) => {
      // The event is broadcast to the whole org channel, so ignore reels for other content.
      if (data.broadcast_id !== broadcastId) {
        return;
      }

      if (data.state === PUSHER_STATES.HIGHLIGHT_REEL_GENERATION_DONE) {
        EventBus.dispatch(CustomEvents.ReloadHighlightReels, {});
        showSuccessToast(PUSHER_STATE_MESSAGES[PUSHER_STATES.HIGHLIGHT_REEL_GENERATION_DONE]);
        return;
      }

      if (data.state === PUSHER_STATES.HIGHLIGHT_REEL_GENERATION_FAILED) {
        showErrorToast(PUSHER_STATE_MESSAGES[PUSHER_STATES.HIGHLIGHT_REEL_GENERATION_FAILED]);
      }
    };

    const listener = EventBus.on(CustomEvents.HighlightReelGenerationStatusUpdated, handler);
    return () => {
      EventBus.off(CustomEvents.HighlightReelGenerationStatusUpdated, listener);
    };
  }, [broadcastId]);
}
